import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Toaster } from '@/components/Toast';
import { Spinner } from '@/components/Spinner';
import { AppShell } from '@/app/AppShell';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { tryBootstrapAuth } from '@/auth/bootstrap';

const LoginPage     = lazy(() => import('@/auth/LoginPage'));
const ClientsPage   = lazy(() => import('@/features/clients/ClientsPage'));
const PosPage       = lazy(() => import('@/features/pos/PosPage'));
const InventoryPage = lazy(() => import('@/features/inventory/InventoryPage'));
const VendorsPage   = lazy(() => import('@/features/vendors/VendorsPage'));
const PaymentsPage  = lazy(() => import('@/features/payments/PaymentsPage'));
const OutstandingPage = lazy(() => import('@/features/outstanding/OutstandingPage'));
const ProformasPage = lazy(() => import('@/features/proformas/ProformasPage'));
const ProformaDetailPage = lazy(() => import('@/features/proformas/ProformaDetailPage'));
const StocktakePage = lazy(() => import('@/features/stocktake/StocktakePage'));
const SyncPage      = lazy(() => import('@/features/sync/SyncPage'));
const SettingsPage  = lazy(() => import('@/features/settings/SettingsPage'));
const EmailLogPage  = lazy(() => import('@/features/email-log/EmailLogPage'));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Spinner className="text-brand-600 h-8 w-8" />
    </div>
  );
}

export function App() {
  // Try to refresh the access token from the HttpOnly refresh cookie before
  // the router renders. This avoids a flicker to /login when the user reloads
  // a PWA window with a still-valid session.
  const [bootDone, setBootDone] = useState(false);
  useEffect(() => {
    tryBootstrapAuth().finally(() => setBootDone(true));
  }, []);

  if (!bootDone) return <LoadingFallback />;

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route index element={<Navigate to="/pos" replace />} />
            <Route path="/pos"        element={<PosPage />} />
            <Route path="/clients"    element={<ClientsPage />} />
            <Route path="/inventory"  element={<InventoryPage />} />
            <Route path="/vendors"    element={<VendorsPage />} />
            <Route path="/payments"   element={<PaymentsPage />} />
            <Route path="/outstanding" element={<OutstandingPage />} />
            <Route path="/proformas"  element={<ProformasPage />} />
            <Route path="/proformas/:id" element={<ProformaDetailPage />} />
            <Route path="/stocktake"  element={<StocktakePage />} />
            <Route path="/sync"       element={<SyncPage />} />
            <Route path="/settings"  element={<SettingsPage />} />
            <Route path="/email-log" element={<EmailLogPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </>
  );
}
